/**
 * Donut chart en SVG con leyenda lateral. Sin dependencias.
 */

interface DataPoint {
  label: string
  value: number
  color?: string
}

interface Props {
  data: DataPoint[]
  size?: number
}

const DEFAULT_COLORS = [
  '#a31b41', // toga
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f43f5e',
  '#64748b',
]

export default function DonutChart({ data, size = 180 }: Props) {
  const total = data.reduce((acc, d) => acc + d.value, 0)
  if (total === 0) {
    return <div className="text-xs t-faint text-center py-8">Sin datos.</div>
  }

  const R = size / 2 - 6
  const r = R * 0.6 // hueco interior
  const cx = size / 2
  const cy = size / 2

  let cumulative = 0
  const segments = data.map((d, i) => {
    const start = cumulative
    cumulative += d.value
    const end = cumulative
    const startA = (start / total) * Math.PI * 2 - Math.PI / 2
    const endA = (end / total) * Math.PI * 2 - Math.PI / 2
    const largeArc = end - start > total / 2 ? 1 : 0
    const x1 = cx + R * Math.cos(startA)
    const y1 = cy + R * Math.sin(startA)
    const x2 = cx + R * Math.cos(endA)
    const y2 = cy + R * Math.sin(endA)
    const x3 = cx + r * Math.cos(endA)
    const y3 = cy + r * Math.sin(endA)
    const x4 = cx + r * Math.cos(startA)
    const y4 = cy + r * Math.sin(startA)
    const path = [
      `M ${x1} ${y1}`,
      `A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${r} ${r} 0 ${largeArc} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ')
    return {
      path,
      color: d.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]!,
      label: d.label,
      value: d.value,
      percent: (d.value / total) * 100,
    }
  })

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((s, i) => (
          <path key={i} d={s.path} fill={s.color}>
            <title>
              {s.label}: {s.value} ({s.percent.toFixed(1)}%)
            </title>
          </path>
        ))}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          className="fill-current text-[18px] font-semibold"
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          className="fill-current text-[9px] opacity-60"
        >
          Total
        </text>
      </svg>

      <ul className="text-xs space-y-1 flex-1 min-w-0">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: s.color }}
            />
            <span className="t-secondary truncate flex-1">{s.label}</span>
            <span className="t-faint font-mono">{s.value}</span>
            <span className="t-faint text-[10px] w-10 text-right">
              {s.percent.toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
